import React, { ReactElement, useCallback, useState } from "react";
import Icon from "@mlb/react-patterns/components/Icon/icon";
import { IconSizes } from "@mlb/react-patterns/components/Icon/icon.types";

import { Context } from "../../../context";
import { Timeline, Ticker } from "../../components/Timeline/Timeline";
import CameraOptionsPopover from "../../components/CameraOptions/CameraOptionsPopover";
import CameraOptionsCycle from "../../components/CameraOptions/CameraOptionsCycle";
import {
  FieldVisionController,
  PublicCameraOption,
} from "../../FieldVisionController";
import {
  PlaybackControlsWrapper,
  PlaybackButton,
  ExitFullscreenButton,
} from "../fieldvision.style";
import { useMedia } from "@mlb/react-patterns/utils/hooks";

const SPEED_OPTIONS = [
  { rate: 1, label: "1x" },
  { rate: 0.5, label: "½x" },
  { rate: 0.25, label: "¼x" },
];

interface PlaybackControlsProps {
  containerRef: React.RefObject<HTMLElement>;
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
  rewind: () => void;
  fastForward: () => void;
  onFullscreen: (enteringFullscreen: boolean) => void;
  onInfoButton: () => void;
  toggleLoop: () => void;
  addSubscriberToContextChange: (
    name: string,
    cb: (context: Context) => void
  ) => void;
  controller: FieldVisionController;
}

let previousContainerStyle: Partial<CSSStyleDeclaration> | undefined =
  undefined;

function PlaybackControls(
  props: PlaybackControlsProps
): ReactElement<PlaybackControlsProps> {
  const { isPlaying, play, pause, addSubscriberToContextChange, controller } =
    props;
  const [currentlyPlaying, setCurrentlyPlaying] = useState(isPlaying);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loopOn, setLoopOn] = useState(true);
  const [speedIndex, setSpeedIndex] = useState(0);

  const isMobile = useMedia({ orientation: "portrait", maxWidth: "750px" });

  addSubscriberToContextChange("playbackControls", (context: Context) => {
    if (isPlaying !== context.state.playing) {
      setCurrentlyPlaying(context.state.playing);
    }
    if (loopOn !== context.clock.loop) {
      setLoopOn(context.clock.loop);
    }
  });

  // Handle when a user interacts with the broser Fullscreen APi (https://developer.mozilla.org/en-US/docs/Web/API/Fullscreen_API)
  document.addEventListener("fullscreenchange", () => {
    if (document.fullscreenElement) {
      enterFullscreen();
    } else {
      exitFullscreen();
    }
  });

  const enterFullscreen = useCallback(() => {
    if (props.containerRef.current) {
      const container = props.containerRef.current;
      previousContainerStyle = {
        width: container.style.width,
        height: container.style.height,
        position: container.style.position,
        zIndex: container.style.zIndex,
        top: container.style.top,
        left: container.style.left,
      };
      container.style.width = "100%";
      container.style.height = "100%";
      container.style.position = "fixed";
      container.style.zIndex = "100";
      container.style.top = "0";
      container.style.left = "0";
      window.dispatchEvent(new Event("resize"));
      setIsFullscreen(true);
    }
  }, [props.containerRef]);

  const exitFullscreen = useCallback(() => {
    if (props.containerRef.current) {
      const container = props.containerRef.current;
      container.style.width = previousContainerStyle?.width!;
      container.style.height = previousContainerStyle?.height!;
      container.style.position = previousContainerStyle?.position!;
      container.style.zIndex = previousContainerStyle?.zIndex!;
      container.style.top = previousContainerStyle?.top!;
      container.style.left = previousContainerStyle?.left!;
      window.dispatchEvent(new Event("resize"));
      setIsFullscreen(false);
    }
  }, [props.containerRef]);

  const playButton = (
    <PlaybackButton
      title={currentlyPlaying ? "pause" : "play"}
      onClick={() => {
        currentlyPlaying ? pause() : play();
        setCurrentlyPlaying(!currentlyPlaying);
      }}
    >
      <Icon
        name={currentlyPlaying ? "pause" : "play"}
        size={IconSizes.MD}
        fill="#fff"
      />
    </PlaybackButton>
  );

  const infoButton = (
    <PlaybackButton
      title="info-fv"
      onClick={() => {
        props.onInfoButton();
      }}
    >
      <Icon name="info-fv" size={IconSizes.MD} fill="#fff" />
    </PlaybackButton>
  );

  const speedButton = (
    <PlaybackButton
      title="playback-rate"
      className="speed"
      onClick={() => {
        const nextSpeed = (speedIndex + 1) % SPEED_OPTIONS.length;
        controller.clock.speed = SPEED_OPTIONS[nextSpeed].rate;
        setSpeedIndex(nextSpeed);
      }}
    >
      <div style={{ minWidth: "19px", fontWeight: "bold", color: "white" }}>
        {SPEED_OPTIONS[speedIndex].label}
      </div>
    </PlaybackButton>
  );

  const ticker = (
    <Ticker
      addSubscriberToContextChange={(
        name: string,
        cb: (context: Context) => void
      ) => controller.addSubscriberToContextChange(name, cb)}
      currentTime={controller.playCurrentTime}
      totalTime={controller.playTotalTime}
      seekToTimelineClick={() => {}}
    />
  );

  const timeline = (
    <Timeline
      addSubscriberToContextChange={(
        name: string,
        cb: (context: Context) => void
      ) => controller.addSubscriberToContextChange(name, cb)}
      currentTime={controller.playCurrentTime}
      totalTime={controller.playTotalTime}
      seekToTimelineClick={(time: number) =>
        controller.seekToTimestamp(controller.playStartTime + time)
      }
    />
  );

  const cameraButton = (
    <CameraOptionsPopover
      setCameraType={(camType: PublicCameraOption) => {
        controller.setCameraMode(camType);
      }}
    />
  );

  const compactCameraButton = (
    <CameraOptionsCycle
      setCameraType={(camType: PublicCameraOption) => {
        controller.setCameraMode(camType);
      }}
    />
  );

  const fullscreenButton = (
    <PlaybackButton
      title="full-screen"
      onClick={() => {
        props.onFullscreen(!isFullscreen);
        isFullscreen ? exitFullscreen() : enterFullscreen();
      }}
    >
      <Icon
        name={!isFullscreen ? "enter-fullscreen" : "exit-fullscreen"}
        size={IconSizes.MD}
        fill="#fff"
      />
    </PlaybackButton>
  );

  const exitFullscreenButton = (
    <ExitFullscreenButton
      onClick={() => {
        props.onFullscreen(false);
        exitFullscreen();
      }}
    >
      <Icon name="close" size={IconSizes.MD} fill="#fff" />
    </ExitFullscreenButton>
  );

  return (
    <div
      id="fvodControls"
      style={{
        position: "absolute",
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
      hidden={true}
    >
      <div>{isFullscreen ? exitFullscreenButton : null}</div>
      {!isMobile || (isMobile && !isFullscreen) ? (
        <PlaybackControlsWrapper>
          {playButton}
          {isFullscreen ? ticker : null}
          {timeline}
          {infoButton}
          {speedButton}
          {compactCameraButton}
          {fullscreenButton}
        </PlaybackControlsWrapper>
      ) : (
        <PlaybackControlsWrapper className="mobile">
          <div className="row top">
            <div className="pair">
              {playButton}
              {isFullscreen ? ticker : null}
            </div>
            <div className="pair">
              {infoButton}
              {speedButton}
              {compactCameraButton}
              {fullscreenButton}
            </div>
          </div>
          <div className="row">{timeline}</div>
        </PlaybackControlsWrapper>
      )}
    </div>
  );
}

export default PlaybackControls;
